import React, { useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AuthLayout } from '../AuthLayout'
import { Notice } from '../../../components/Notice'
import { Icon } from '../../../components/Icon'
import { VerifyRecoveryCodeFunc, SignInFunc, SignInSuccessFunc } from '../../types'

export type AccountRecoveryProps = {
  onVerifyRecoveryCode: VerifyRecoveryCodeFunc
  onSignIn: SignInFunc
  onSignInSuccess: SignInSuccessFunc
  email: string
  fullWidth?: boolean
}

export function AccountRecovery({
  onSignIn,
  onSignInSuccess,
  onVerifyRecoveryCode,
  email,
  fullWidth,
}: AccountRecoveryProps): JSX.Element {
  const { t } = useTranslation('cognito')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [sentEmailVerifyRequest, setSentEmailVerifyRequest] = useState<boolean>(false)
  const [emailVerificationCode, setEmailVerificationCode] = useState<string>('')
  const [recoveryCode, setRecoveryCode] = useState<string>('')

  async function handleSendEmailVerifyRequest(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()

    if (!email) return

    setError('')
    setLoading(true)

    try {
      const challenge = await onSignIn(email)
      console.log('SIGN IN RETURNED')
      console.log('Challenge:')
      console.log(challenge)
      if (challenge && challenge.includes('CUSTOM_CHALLENGE')) {
        console.log('setSentEmail')
        setSentEmailVerifyRequest(true)
      }
    } catch (localError) {
      // console.log('Error: ')
      // console.log(error)
      setError(localError.message)
    }

    setLoading(false)
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()

    if (!email) return

    setError('')
    setLoading(true)

    try {
      const result = await onVerifyRecoveryCode(emailVerificationCode, recoveryCode)
      if (result.error) {
        setError(result.error.message)
      } else if (result.cognitoUser) {
        onSignInSuccess(result.cognitoUser)
      } else {
        setError('Account recovery did not complete, please try again or contact support.')
      }
    } catch (e) {
      setError(t(`pages.auth-mfa.errors.${e.code}`))
    }

    setLoading(false)
  }

  if (!email) return <></>
  console.log('sentEmailVerifyRequest:')
  console.log(sentEmailVerifyRequest)

  return (
    <>
      <AuthLayout i18nKey="pages.account-recovery.title" fullWidth={fullWidth}>
        {sentEmailVerifyRequest ? (
          <>
            <form onSubmit={handleSubmit}>
              {error && (
                <Notice severity="error" fullWidth gutterBottom>
                  {error}
                </Notice>
              )}
              <Box my={4}>{t('pages.account-recovery.description')}</Box>
              <Box my={2}>
                <TextField
                  autoCapitalize="none"
                  autoComplete="off"
                  autoCorrect="off"
                  autoFocus
                  disabled={loading}
                  fullWidth
                  id="verification-code"
                  // maxLength={6}
                  label={t('pages.account-recovery.email-recovery-code-placeholder')}
                  onChange={e => setEmailVerificationCode(e.currentTarget.value.trim())}
                  InputProps={{ disableUnderline: true }}
                  required
                  value={emailVerificationCode}
                  variant="filled"
                />
              </Box>
              <Box my={2}>
                <TextField
                  autoCapitalize="none"
                  autoComplete="off"
                  autoCorrect="off"
                  disabled={loading}
                  fullWidth
                  id="recovery-code"
                  // maxLength={36}
                  label={t('pages.account-recovery.recovery-code-placeholder')}
                  onChange={e => setRecoveryCode(e.currentTarget.value.trim())}
                  InputProps={{ disableUnderline: true }}
                  required
                  value={recoveryCode}
                  variant="filled"
                />
              </Box>
              <Box my={4} textAlign="right">
                <Button
                  disabled={loading || emailVerificationCode.length < 6 || recoveryCode.length < 36}
                  type="submit"
                >
                  {t('global.actions.submit')}
                  <Icon inline spin={loading} name={loading ? 'spinner-third' : 'arrow-right'} />
                </Button>
              </Box>
            </form>
            <SupportRecoveryRequest />
          </>
        ) : (
          <>
            <form onSubmit={handleSendEmailVerifyRequest}>
              <Box mt={4}>
                <Typography>{t('pages.account-recovery.account-recovery-code')} </Typography>
              </Box>
              <Box mb={4} mt={2}>
                <Button disabled={loading} type="submit">
                  {t('pages.account-recovery.start-recovery')}
                </Button>
              </Box>
            </form>
            <Box my={4}>
              <SupportRecoveryRequest />
            </Box>
          </>
        )}
      </AuthLayout>
    </>
  )
}

function SupportRecoveryRequest(): JSX.Element {
  const { t } = useTranslation('cognito')
  return (
    <Box>
      <Box my={2}>
        <Typography>
          <strong>{t('pages.account-recovery.lost-recovery-code-heading')}</strong>
        </Typography>
      </Box>
      <Typography>{t('pages.account-recovery.lost-recovery-code')}</Typography>
      <Box mt={2}>
        <Button
          href={encodeURI(
            'mailto:support@remote.it?subject=Recover my Remote.It account&body=Please reset my account preferences.\n\nI no longer have access to the recovery code and the mobile device to receive verification codes.'
          )}
        >
          {t('pages.account-recovery.lost-recovery-code-button')}
        </Button>
      </Box>
    </Box>
  )
}
